import React, { useCallback, useMemo, useState } from 'react';

import dayjs from '~/timeInit';

import {
  Add as AddIcon,
  Code as CodeIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  ListAlt as ListAltIcon,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  DataGridPro,
  type GridColDef,
  type GridPaginationModel,
  type GridRenderCellParams,
} from '@mui/x-data-grid-pro';

import { useDeleteClaimCampaign, useListClaimCampaigns } from '~/hooks/apis/claimCampaigns';

import {
  CLAIM_CAMPAIGN_STATUSES,
  type ClaimCampaign,
  type ClaimCampaignStatus,
  type ListClaimCampaignsParams,
} from '~/types/claimCampaignTypes';

import ClaimCodesDialog from './ClaimCodesDialog';
import ClaimRecordsDialog from './ClaimRecordsDialog';
import CreateClaimCampaignDialog from './CreateClaimCampaignDialog';
import UploadClaimCodesDialog from './UploadClaimCodesDialog';

const DEFAULT_PAGE_SIZE = 25;

/** Maps campaign status to a MUI Chip color */
function getStatusChipColor(status: ClaimCampaignStatus): 'default' | 'success' | 'warning' | 'info' | 'error' {
  switch (status) {
    case 'ACTIVE':
      return 'success';
    case 'DRAFT':
      return 'info';
    case 'PAUSED':
      return 'warning';
    case 'EXPIRED':
      return 'default';
    default:
      return 'default';
  }
}

/** Formats a validator type for display */
function formatValidatorType(type: string): string {
  switch (type) {
    case 'PREGEN_CODE':
      return 'Pre-gen Code';
    case 'SHARED_CODE':
      return 'Shared Code';
    case 'EXTERNAL_API':
      return 'External API';
    default:
      return type;
  }
}

export default function LoyaltyClaimCampaignsPage() {
  const theme = useTheme();
  const themeColor = theme.palette.primary.main;

  const deleteMutation = useDeleteClaimCampaign();

  // Pagination state
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });

  // Filter inputs
  const [statusInput, setStatusInput] = useState<ClaimCampaignStatus | ''>('');

  // Applied filters
  const [filters, setFilters] = useState<ListClaimCampaignsParams>({
    status: undefined,
    limit: DEFAULT_PAGE_SIZE,
    offset: 0,
  });

  // Dialog states
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isUploadCodesOpen, setIsUploadCodesOpen] = useState(false);
  const [isCodesOpen, setIsCodesOpen] = useState(false);
  const [isClaimsOpen, setIsClaimsOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState<ClaimCampaign | null>(null);

  // Build query params from pagination + filters
  const queryParams: ListClaimCampaignsParams = useMemo(
    () => ({
      status: filters.status,
      limit: paginationModel.pageSize,
      offset: paginationModel.page * paginationModel.pageSize,
    }),
    [paginationModel, filters],
  );

  const { data, isLoading, error } = useListClaimCampaigns(queryParams);

  const campaigns: ClaimCampaign[] = data?.result?.campaigns || [];
  const totalCount = data?.result?.pagination?.total ?? 0;

  /** Apply search filters and reset to first page */
  const handleSearch = useCallback(() => {
    setFilters({
      status: statusInput || undefined,
      limit: paginationModel.pageSize,
      offset: 0,
    });
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, [statusInput, paginationModel.pageSize]);

  /** Clear all filters */
  const handleClearFilters = useCallback(() => {
    setStatusInput('');
    setFilters({ status: undefined, limit: DEFAULT_PAGE_SIZE, offset: 0 });
    setPaginationModel({ page: 0, pageSize: DEFAULT_PAGE_SIZE });
  }, []);

  const handlePaginationModelChange = useCallback((model: GridPaginationModel) => {
    setPaginationModel((prev) => {
      if (prev.page === model.page && prev.pageSize === model.pageSize) {
        return prev;
      }
      return model;
    });
  }, []);

  // Campaign action handlers
  const handleOpenUploadCodes = useCallback((campaign: ClaimCampaign) => {
    setSelectedCampaign(campaign);
    setIsUploadCodesOpen(true);
  }, []);

  const handleOpenCodes = useCallback((campaign: ClaimCampaign) => {
    setSelectedCampaign(campaign);
    setIsCodesOpen(true);
  }, []);

  const handleOpenClaims = useCallback((campaign: ClaimCampaign) => {
    setSelectedCampaign(campaign);
    setIsClaimsOpen(true);
  }, []);

  const handleOpenEdit = useCallback((campaign: ClaimCampaign) => {
    setSelectedCampaign(campaign);
    setIsEditOpen(true);
  }, []);

  const handleOpenDeleteConfirm = useCallback((campaign: ClaimCampaign) => {
    setSelectedCampaign(campaign);
    setIsDeleteConfirmOpen(true);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!selectedCampaign) return;
    try {
      await deleteMutation.mutateAsync(selectedCampaign.id);
      setIsDeleteConfirmOpen(false);
    } catch {
      // Error handled in mutation hook
    }
  }, [selectedCampaign, deleteMutation]);

  const columns: Array<GridColDef<ClaimCampaign>> = useMemo(
    () => [
      {
        field: 'id',
        headerName: 'ID',
        width: 140,
      },
      {
        field: 'name',
        headerName: 'Name',
        width: 180,
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 110,
        renderCell: ({ value }: GridRenderCellParams<ClaimCampaign>) => (
          <Chip label={value} size="small" color={getStatusChipColor(value as ClaimCampaignStatus)} />
        ),
      },
      {
        field: 'validatorType',
        headerName: 'Validator',
        width: 120,
        valueFormatter: (value: string) => formatValidatorType(value),
      },
      {
        field: 'maxClaimsPerUser',
        headerName: 'Max Claims',
        width: 90,
        type: 'number',
      },
      {
        field: 'startDate',
        headerName: 'Start',
        width: 130,
        valueFormatter: (value: string | null) => (value ? dayjs(value).format('DD/MM/YYYY HH:mm') : '-'),
      },
      {
        field: 'endDate',
        headerName: 'End',
        width: 130,
        valueFormatter: (value: string | null) => (value ? dayjs(value).format('DD/MM/YYYY HH:mm') : '-'),
      },
      {
        field: 'createdAt',
        headerName: 'Created',
        width: 110,
        valueFormatter: (value: string) => dayjs(value).format('DD/MM/YYYY'),
      },
      {
        field: 'actions',
        headerName: '',
        width: 240,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: ({ row }: GridRenderCellParams<ClaimCampaign>) => {
          const isExternalApi = row.validatorType === 'EXTERNAL_API';
          return (
            <Stack direction="row" spacing={0.5}>
              <Tooltip title="Edit campaign">
                <IconButton
                  size="small"
                  onClick={(event) => {
                    event.stopPropagation();
                    handleOpenEdit(row);
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={isExternalApi ? 'External API campaigns cannot have codes' : 'Upload codes'}>
                <span>
                  <IconButton
                    size="small"
                    disabled={isExternalApi}
                    onClick={(event) => {
                      event.stopPropagation();
                      handleOpenUploadCodes(row);
                    }}
                  >
                    <AddIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="View codes">
                <IconButton
                  size="small"
                  onClick={(event) => {
                    event.stopPropagation();
                    handleOpenCodes(row);
                  }}
                >
                  <CodeIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="View claims">
                <IconButton
                  size="small"
                  onClick={(event) => {
                    event.stopPropagation();
                    handleOpenClaims(row);
                  }}
                >
                  <ListAltIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete">
                <IconButton
                  size="small"
                  onClick={(event) => {
                    event.stopPropagation();
                    handleOpenDeleteConfirm(row);
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          );
        },
      },
    ],
    [handleOpenEdit, handleOpenUploadCodes, handleOpenCodes, handleOpenClaims, handleOpenDeleteConfirm],
  );

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Paper
        elevation={1}
        sx={{
          p: 4,
          mb: 4,
          position: 'sticky',
          top: 0,
          zIndex: 10,
          bgcolor: 'background.paper',
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography
              variant="h4"
              sx={{
                mb: 0.5,
                fontWeight: theme.typography.fontWeightBold,
                background: `linear-gradient(135deg, ${themeColor}, ${alpha(themeColor, 0.7)})`,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Claim Campaigns
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage claim campaigns, codes, and customer claims
            </Typography>
          </Box>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setIsCreateOpen(true)}>
            Create Campaign
          </Button>
        </Box>
      </Paper>

      {/* Filters */}
      <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
          <TextField
            select
            label="Status"
            value={statusInput}
            onChange={(event) => setStatusInput(event.target.value as ClaimCampaignStatus | '')}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="">All Statuses</MenuItem>
            {CLAIM_CAMPAIGN_STATUSES.map((status) => (
              <MenuItem key={status} value={status}>
                {status}
              </MenuItem>
            ))}
          </TextField>
          <Stack direction="row" spacing={1} alignItems="center">
            <Button variant="contained" onClick={handleSearch}>
              Search
            </Button>
            <Button variant="text" onClick={handleClearFilters}>
              Clear
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {/* Error alert */}
      {error && (
        <Box mb={3}>
          <Alert severity="error">
            Error loading claim campaigns: {error instanceof Error ? error.message : String(error)}
          </Alert>
        </Box>
      )}

      {/* Campaign data grid */}
      <Paper elevation={0} sx={{ height: 650 }}>
        <DataGridPro
          rows={campaigns}
          columns={columns}
          loading={isLoading}
          pagination
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={handlePaginationModelChange}
          pageSizeOptions={[10, 25, 50, 100]}
          rowCount={totalCount}
          getRowId={(row) => row.id}
          disableRowSelectionOnClick
          sx={{
            height: '100%',
            '& .MuiDataGrid-row:hover': {
              bgcolor: alpha(themeColor, 0.08),
            },
          }}
        />
      </Paper>

      {/* Dialogs */}
      <CreateClaimCampaignDialog open={isCreateOpen} onClose={() => setIsCreateOpen(false)} />

      <CreateClaimCampaignDialog open={isEditOpen} campaign={selectedCampaign} onClose={() => setIsEditOpen(false)} />

      <UploadClaimCodesDialog
        open={isUploadCodesOpen}
        campaign={selectedCampaign}
        onClose={() => setIsUploadCodesOpen(false)}
      />

      <ClaimCodesDialog open={isCodesOpen} campaign={selectedCampaign} onClose={() => setIsCodesOpen(false)} />

      <ClaimRecordsDialog open={isClaimsOpen} campaign={selectedCampaign} onClose={() => setIsClaimsOpen(false)} />

      {/* Delete confirmation dialog */}
      <Dialog open={isDeleteConfirmOpen} onClose={() => setIsDeleteConfirmOpen(false)}>
        <DialogTitle>Delete Campaign</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the campaign &quot;{selectedCampaign?.name}&quot;? This will delete all
            related codes and claims. This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => setIsDeleteConfirmOpen(false)} disabled={deleteMutation.isPending}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={handleConfirmDelete} disabled={deleteMutation.isPending}>
            {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
